import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createHeaders, getApiUrls } from '../services/serviceConfig';
import { mapSessionTypeToChallenge } from '../utils/challengeTypeUtils';

type Lesson = {
  title: string;
};

type ActivityInstance = {
  id: string;
  respondedAt?: string | null;
};

type StudentSession = {
  activityInstances: ActivityInstance[];
  id: string;
  lessons?: Lesson[];
  maxActivityNumber: number;
  sessionTitle?: string;
  sessionType?: string;
  status?: string;
  subject?: string;
};

type Recommendation = {
  activitiesCompleted: number;
  challengeType: string;
  completed: boolean;
  icon: string;
  id: string;
  sessionData: StudentSession;
  subtitle: string;
  title: string;
  totalActivities: number;
};

type UseStudentRecommendationsResult = {
  error: Error | null;
  generateSessions: () => Promise<void>;
  isGenerating: boolean;
  isInitialLoading: boolean;
  recommendations: Recommendation[];
  refreshRecommendations: () => Promise<void>;
};

function calculateActivitiesCompleted(session: StudentSession): number {
  return session.activityInstances?.filter((activity) => activity.respondedAt).length ?? 0;
}

function removeParagraphTags(html: string): string {
  return html.replace(/<\/?p[^>]*>/gi, '').trim();
}

function prepareLessonsSubtitle(lessons?: Lesson[]): string {
  if (!lessons?.length) return '';

  const lessonTitles = lessons.map((lesson) => removeParagraphTags(lesson.title)).filter(Boolean);

  if (!lessonTitles.length) return '';

  return `<p>${lessonTitles.join(', ')}</p>`;
}

function useStudentRecommendations(): UseStudentRecommendationsResult {
  const { i18n, t } = useTranslation(['chatbot']);
  const [error, setError] = useState<Error | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<StudentSession[]>([]);
  const isMountedRef = useRef<boolean>(true);

  const refreshRecommendations = useCallback(async () => {
    setError(null);

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.getSessions(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        setSessions([]);
        setError(new Error(`Failed to fetch recommendations: HTTP ${response.status}`));
        return;
      }

      const data = (await response.json()) as StudentSession[];
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      setSessions([]);
      setError(err instanceof Error ? err : new Error('Failed to fetch recommendations'));
    } finally {
      if (isMountedRef.current) setIsInitialLoading(false);
    }
  }, []);

  const generateSessions = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.generateSession(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        setError(new Error(`Failed to generate sessions: HTTP ${response.status}`));
        return;
      }

      await refreshRecommendations();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to generate sessions'));
    } finally {
      setIsGenerating(false);
    }
  }, [refreshRecommendations]);

  const recommendations = useMemo<Recommendation[]>(() => {
    if (!sessions.length) return [];

    const cardTitles = t('student_recommendations_card_titles', { returnObjects: true });

    return sessions.map((session) => {
      const activitiesCompleted = calculateActivitiesCompleted(session);
      const subtitleForLessons = prepareLessonsSubtitle(session.lessons);

      const titleTranslation =
        typeof cardTitles === 'object' && cardTitles && session.sessionTitle
          ? (cardTitles as Record<string, string>)[session.sessionTitle] || session.sessionTitle
          : session.sessionTitle || '';

      return {
        id: session.id,
        title: titleTranslation,
        subtitle: subtitleForLessons,
        icon: '📊',
        challengeType: mapSessionTypeToChallenge(session.sessionType),
        totalActivities: session.maxActivityNumber,
        activitiesCompleted,
        completed: session.status === 'COMPLETED',
        sessionData: session,
      };
    });
  }, [i18n.language, sessions, t]);

  useEffect(() => {
    isMountedRef.current = true;
    refreshRecommendations();

    return function () {
      isMountedRef.current = false;
    };
  }, [refreshRecommendations]);

  return {
    recommendations,
    isInitialLoading,
    isGenerating,
    error,
    refreshRecommendations,
    generateSessions,
  };
}

export default useStudentRecommendations;
